import { type PaginationState } from '@tanstack/react-table'
import clsx from 'clsx'
import { Plus, X } from 'lucide-react'
import {
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useState,
} from 'react'

import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from 'components/brokers/types'
import AnimateHeight from 'components/reusable/AnimateHeight'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import OpacityTransition from 'components/reusable/OpacityTransition'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterGroup,
  type ComboboxFilterOption,
} from 'components/reusable/table/ComboboxMultiSelectFilter'
import Tooltip from 'components/reusable/Tooltip'
import { Badge, Button, IconButton } from 'slab'

export interface FilterConfig<T, G extends string> {
  key: keyof T
  label: string
  group: G
  isMultiValue: boolean
  tooltip?: string
}

export interface FilterConfigurations<T, G extends string> {
  [key: string]: FilterConfig<T, G>
}

type SingleSelectFilterComponents = Record<string, FC<SingleSelectedValueFilter>>
type MultiSelectFilterComponents = Record<string, FC<MultipleSelectedValuesFilter>>

export interface EnterpriseFiltersViewProps<T extends Record<string, unknown>, G extends string> {
  filters: T
  setFilters: Dispatch<SetStateAction<T>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  filterConfigurations: FilterConfigurations<T, G>
  singleSelectFilters: SingleSelectFilterComponents
  multiSelectFilters: MultiSelectFilterComponents
  defaultPinnedOptionsByFilterValue?: Record<string, Array<{ label: string; value: string }>>
  getFiltersConfigurationSortValue: (filterConfiguration: ComboboxFilterGroup<string>) => number
  showFilterBadges?: boolean
}

function generateFilterGroups<T extends Record<string, unknown>, G extends string> (
  filterConfigurations: FilterConfig<T, G>[],
  currentFilters: T = {} as T
): ComboboxFilterGroup<string>[] {
  const optionsGroupedByLabel = filterConfigurations.reduce<
    Record<G, ComboboxFilterOption<string>[]>
  >((acc, filter) => {
    const { group, key, label } = filter

    const existingGroup = acc[group] || []

    // Disable the filter if its already in use.
    const disabled = !!currentFilters[key]

    existingGroup.push({ value: key.toString(), label, disabled })
    acc[group] = existingGroup
    return acc
  }, {} as Record<G, ComboboxFilterOption<string>[]>)

  return Object.entries(optionsGroupedByLabel)
    .map(([groupLabel, options]) => ({
      groupLabel,
      options: options as ComboboxFilterOption<string>[],
    }))
}

function useToggleSelection<T extends Record<string, unknown>> (
  pagination: PaginationState,
  setPagination: Dispatch<SetStateAction<PaginationState>>
) {
  return useCallback(
    (
      value: string,
      key: keyof T,
      setSelectedItems: Dispatch<SetStateAction<T>>
    ) => {
      setSelectedItems((prev) => {
        const existing = (prev[key] as string[]) ?? []

        // If the item is already in the list, remove it from the list. Otherwise, add it! This allows users to toggle.
        const updated = existing.includes(value)
          ? existing.filter((item: string) => item !== value)
          : [...existing, value]

        return {
          ...prev,
          [key]: updated,
        }
      })

      if (pagination.pageIndex !== 1) {
        setPagination({ ...pagination, pageIndex: 1 })
      }
    },
    [pagination, setPagination]
  )
}

type FilterProps<T extends Record<string, unknown>, G extends string> = {
  config: FilterConfig<T, G>
  children: ReactNode
  setFilters: Dispatch<SetStateAction<T>>
  showBadge?: boolean
}

const Filter = <T extends Record<string, unknown>, G extends string>({
  config,
  children,
  setFilters,
  showBadge = true,
}: FilterProps<T, G>) => {
  const { label, key, tooltip } = config

  const handleRemoveFilter = () => {
    setFilters((prev) => {
      const newFilters = { ...prev }

      delete newFilters[key]

      return newFilters
    })
  }

  const labelElement = (
    <div className='font-semibold text-sm min-w-[150px] ml-2'>{label}</div>
  )

  return (
    <div
      className={clsx({
        'p-1 my-1 rounded-md max-w-[600px] flex items-center justify-between gap-2 border shadow-sm':
          true,
      })}
    >
      {tooltip
        ? (
          <Tooltip triggerChildren={labelElement} side='top'>
            {tooltip}
          </Tooltip>
          )
        : (
            labelElement
          )}
      {showBadge && (
        <div className='min-w-[70px]'>
          <FilterBadge />
        </div>
      )}
      {children}
      <div className='flex-grow-1 flex-shrink-0'>
        <IconButton
          onClick={handleRemoveFilter}
          variant='destructive'
          label='Remove filter'
          icon={X}
        />
      </div>
    </div>
  )
}

const FilterBadge = () => <Badge type='info'>Filter</Badge>

const EnterpriseFiltersView = <T extends Record<string, unknown>, G extends string>({
  filters,
  setFilters,
  pagination,
  setPagination,
  filterConfigurations,
  singleSelectFilters,
  multiSelectFilters,
  defaultPinnedOptionsByFilterValue = {},
  getFiltersConfigurationSortValue,
  showFilterBadges = true,
}: EnterpriseFiltersViewProps<T, G>) => {
  const [selectedFilterType, setSelectedFilterType] = useState<boolean>(false)
  const [query, setQuery] = useState('')

  const filteredConfigurationOptions = Object.values(filterConfigurations).filter(
    ({ key, label }) =>
      label?.toLowerCase().includes(query.toLowerCase()) ||
      key.toString().toLowerCase().includes(query.toLowerCase())
  )

  const toggleSelection = useToggleSelection<T>(pagination, setPagination)

  const onSelectFilterType = (value: string) => {
    if (!selectedFilterType) return

    const filterType = value as keyof T

    setFilters((prev) => {
      const filterConfig = filterConfigurations[filterType.toString()]
      const { key, isMultiValue } = filterConfig

      if (isMultiValue) {
        return {
          ...prev,
          [key]: [],
        }
      } else {
        return {
          ...prev,
          [key]: '',
        }
      }
    })
    setSelectedFilterType(false)
  }

  const sortedFilterGroups = generateFilterGroups(filteredConfigurationOptions, filters).sort(
    (a, b) => getFiltersConfigurationSortValue(a) - getFiltersConfigurationSortValue(b)
  )

  return (
    <div className='space-y-2'>
      <AnimateHeight>
        <div className='flex flex-wrap items-center justify-start gap-1 flex-shrink-0'>
          {Object.entries(filters)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([filter, selectedValues]) => {
              const config = filterConfigurations[filter]
              const { isMultiValue } = config

              if (isMultiValue) {
                const FilterComponent = multiSelectFilters[filter]
                const onSelectValue = (value: string) => {
                  toggleSelection(value, filter as keyof T, setFilters)
                }

                const defaultPinnedOptions = defaultPinnedOptionsByFilterValue[filter] || []

                const filterProps = {
                  filterKey: filter,
                  selectedValues: selectedValues as string[],
                  isMultiValue,
                  onSelectValue,
                  defaultPinnedOptions,
                }

                return (
                  <AnimateOpacity key={`${filter}`} isOpen>
                    <Filter key={`${filter}`} config={config} setFilters={setFilters} showBadge={showFilterBadges}>
                      <div className='bg-white'>
                        {FilterComponent && <FilterComponent {...filterProps} />}
                      </div>
                    </Filter>
                  </AnimateOpacity>
                )
              } else {
                const FilterComponent = singleSelectFilters[filter]
                const selectValue = (value: string) => {
                  setFilters((prev) => ({
                    ...prev,
                    [filter]: value,
                  }))
                  if (pagination.pageIndex !== 1) {
                    setPagination({ ...pagination, pageIndex: 1 })
                  }
                }

                const filterProps = {
                  filterKey: filter,
                  selectedValue: selectedValues as string,
                  isMultiValue,
                  selectValue,
                }

                return (
                  <AnimateOpacity key={filter} isOpen>
                    <Filter key={filter} config={config} setFilters={setFilters} showBadge={showFilterBadges}>
                      <div className='bg-white'>
                        {FilterComponent && <FilterComponent {...filterProps} />}
                      </div>
                    </Filter>
                  </AnimateOpacity>
                )
              }
            })}
        </div>
      </AnimateHeight>

      <div className='ml-1 pb-4 h-8'>
        {!selectedFilterType && (
          <OpacityTransition key='add-filter'>
            <div className='font-semibold text-sm flex items-center justify-start space-x-2'>
              <div>Add filter:</div>
              <Button variant='tertiary' onClick={() => setSelectedFilterType(true)}>
                <div className='flex items-center justify-center space-x-2'>
                  <div>Filter</div>
                  <div>
                    <Plus size={16} />
                  </div>
                </div>
              </Button>
            </div>
          </OpacityTransition>
        )}

        {selectedFilterType && (
          <OpacityTransition key='include'>
            <div className='flex items-center justify-start space-x-2 text-sm font-semibold'>
              {showFilterBadges && <FilterBadge />}
              <ComboboxMultiSelectFilter
                placeholder='Search filter types...'
                options={sortedFilterGroups}
                selectedValues={[]}
                onSelectValue={onSelectFilterType}
                onValueChange={setQuery}
                isOpen
              >
                <Button variant='tertiary'>Select a filter type...</Button>
              </ComboboxMultiSelectFilter>
              <IconButton
                onClick={() => setSelectedFilterType(false)}
                variant='destructive'
                label='Remove filter'
                icon={X}
              />
            </div>
          </OpacityTransition>
        )}
      </div>
    </div>
  )
}

export default EnterpriseFiltersView
